/**
 * Discount-based Product Auto-Add/Remove Script
 * Automatically adds or removes products from cart based on applied discount codes
 */

(function () {
  'use strict';

  // Configuration from theme settings
  const config = window.Shopify?.theme?.discountAutoAdd || {};

  if (!config.enabled) {
    return; // Exit if feature is disabled
  }

  const DEBUG = config.debugMode || false;
  const AUTO_REMOVE = config.autoRemove || false;
  const DISCOUNT_MAPPING = config.mapping || {};

  // Logging helper
  function log(...args) {
    if (DEBUG) {
      console.log('[Cart-Extra]', ...args);
    }
  }

  // Track pending requests to avoid race conditions
  let pendingRequests = 0;
  let processingQueue = [];
  let isProcessing = false;
  let lastCartState = null;
  let requestCounter = 0;
  let currentlyAddingVariants = new Set(); // Track variants being added
  let currentlyRemovingVariants = new Set(); // Track variants being removed
  let processCartTimeout = null; // Debounce timeout for cart processing

  /**
   * Refresh cart UI by dispatching events and calling cart refresh methods
   */
  function refreshCart() {
    log('Refreshing cart UI...');

    // Dispatch custom cart update event for Shopify themes
    try {
      document.dispatchEvent(new CustomEvent('cart:updated'));
      log('Dispatched cart:updated event');
    } catch (e) {
      log('Failed to dispatch cart:updated event:', e);
    }

    // Dispatch cart refresh event
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      log('Dispatched cart:refresh event');
    } catch (e) {
      log('Failed to dispatch cart:refresh event:', e);
    }

    // Call upcart refresh if available
    if (typeof window.upcartRefreshCart === 'function') {
      try {
        window.upcartRefreshCart();
        log('Called window.upcartRefreshCart()');
      } catch (e) {
        log('Failed to call upcartRefreshCart:', e);
      }
    }

    // Trigger cart drawer refresh if available
    if (typeof window.refreshCart === 'function') {
      try {
        window.refreshCart();
        log('Called window.refreshCart()');
      } catch (e) {
        log('Failed to call refreshCart:', e);
      }
    }
  }

  /**
   * Get variant IDs that should be in cart based on applied discounts
   */
  function getRequiredVariants(discountCodes) {
    const variants = new Set();

    discountCodes.forEach((code) => {
      const upperCode = code.toUpperCase();
      const mappedVariants = DISCOUNT_MAPPING[upperCode];

      if (mappedVariants && Array.isArray(mappedVariants)) {
        mappedVariants.forEach((variantId) => variants.add(variantId.toString()));
        log(`Discount "${code}" requires variants:`, mappedVariants);
      }
    });

    return variants;
  }

  /**
   * Get variant IDs that are currently in cart and managed by this script
   */
  function getManagedVariantsInCart(cartItems) {
    const allManagedVariants = new Set();

    // Collect all variants from all discount mappings
    Object.values(DISCOUNT_MAPPING).forEach((variants) => {
      if (Array.isArray(variants)) {
        variants.forEach((id) => allManagedVariants.add(id.toString()));
      }
    });

    // Filter cart items to only managed variants
    return cartItems
      .filter((item) => allManagedVariants.has(item.variant_id.toString()))
      .map((item) => ({
        variantId: item.variant_id.toString(),
        key: item.key,
        quantity: item.quantity,
      }));
  }

  function getAppliedDiscounts(cartData) {
    return (cartData.cart_level_discount_applications || [])
      .map((d) => d.title)
      .concat((cartData.discount_codes || []).map((d) => (d.applicable ? d.code : null)))
      .filter(Boolean);
  }

  /**
   * Process cart data and perform necessary additions/removals
   */
  async function processCart(cartData) {
    if (isProcessing) {
      log('Already processing, queuing request');
      return;
    }

    isProcessing = true;
    const requestId = ++requestCounter;

    try {
      log(`[${requestId}] Processing cart:`, cartData);

      // Get applied discount codes
      const appliedDiscounts = getAppliedDiscounts(cartData);

      log(`[${requestId}] Applied discounts:`, appliedDiscounts);

      // Get variants that should be in cart
      const requiredVariants = getRequiredVariants(appliedDiscounts);
      log(`[${requestId}] Required variants:`, Array.from(requiredVariants));

      // Get managed variants currently in cart
      const managedInCart = getManagedVariantsInCart(cartData.items || []);
      log(`[${requestId}] Managed variants in cart:`, managedInCart);

      const inCartVariants = new Set(managedInCart.map((item) => item.variantId));

      // Variants to add (required but not in cart AND not currently being added)
      const toAdd = Array.from(requiredVariants).filter(
        (id) => !inCartVariants.has(id) && !currentlyAddingVariants.has(id)
      );

      // Variants to remove (in cart but not required, only if AUTO_REMOVE is enabled AND not currently being removed)
      const toRemove = AUTO_REMOVE
        ? managedInCart.filter(
            (item) => !requiredVariants.has(item.variantId) && !currentlyRemovingVariants.has(item.variantId)
          )
        : [];

      log(`[${requestId}] To add:`, toAdd);
      log(`[${requestId}] Currently adding:`, Array.from(currentlyAddingVariants));
      log(
        `[${requestId}] To remove:`,
        toRemove.map((i) => i.variantId)
      );
      log(`[${requestId}] Currently removing:`, Array.from(currentlyRemovingVariants));

      // Track if any changes were made
      let changesMade = false;

      // Perform additions
      for (const variantId of toAdd) {
        // Mark as currently adding
        currentlyAddingVariants.add(variantId);

        try {
          log(`[${requestId}] Adding variant ${variantId}...`);
          await addToCart(variantId);
          log(`[${requestId}] Successfully added variant ${variantId}`);
          changesMade = true;
        } catch (error) {
          console.error(`[Cart-Extra][${requestId}] Failed to add variant ${variantId}:`, error);
        } finally {
          // Remove from currently adding after a delay to ensure cart state is updated
          setTimeout(() => {
            currentlyAddingVariants.delete(variantId);
            log(`[${requestId}] Cleared adding lock for variant ${variantId}`);
          }, 1000);
        }
      }

      // Perform removals
      for (const item of toRemove) {
        // Mark as currently removing
        currentlyRemovingVariants.add(item.variantId);

        try {
          log(`[${requestId}] Removing variant ${item.variantId}...`);
          await removeFromCart(item.key);
          log(`[${requestId}] Successfully removed variant ${item.variantId}`);
          changesMade = true;
        } catch (error) {
          console.error(`[Cart-Extra][${requestId}] Failed to remove variant ${item.variantId}:`, error);
        } finally {
          // Remove from currently removing after a delay
          setTimeout(() => {
            currentlyRemovingVariants.delete(item.variantId);
            log(`[${requestId}] Cleared removing lock for variant ${item.variantId}`);
          }, 1000);
        }
      }

      // Refresh cart UI if changes were made
      if (changesMade) {
        log(`[${requestId}] Changes made, refreshing cart UI...`);
        setTimeout(() => refreshCart(), 300);
      }

      // Update last cart state
      lastCartState = {
        discounts: appliedDiscounts,
        requiredVariants: Array.from(requiredVariants),
        timestamp: Date.now(),
      };
    } finally {
      isProcessing = false;

      // Process next item in queue if any
      if (processingQueue.length > 0) {
        const next = processingQueue.shift();
        setTimeout(() => processCart(next), 100);
      }
    }
  }

  /**
   * Add product to cart
   */
  function addToCart(variantId) {
    return fetch('/cart/add.js?upcart=1&opens_cart=never', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
      }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    });
  }

  /**
   * Remove product from cart
   */
  function removeFromCart(lineItemKey) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: lineItemKey,
        quantity: 0,
      }),
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    });
  }

  /**
   * Handle cart response and trigger processing
   */
  function handleCartResponse(cartData) {
    if (!cartData || typeof cartData !== 'object') {
      return;
    }

    // Check if this cart state is different from last processed
    const currentDiscounts = getAppliedDiscounts(cartData);

    const discountsChanged =
      !lastCartState ||
      JSON.stringify(currentDiscounts.sort()) !== JSON.stringify((lastCartState.discounts || []).sort());

    // Check if required products were removed from cart
    let productRemoved = false;
    if (lastCartState && lastCartState.requiredVariants && lastCartState.requiredVariants.length > 0) {
      const currentManagedInCart = getManagedVariantsInCart(cartData.items || []);
      const currentManagedVariantIds = new Set(currentManagedInCart.map((item) => item.variantId));

      // Check if any required variant is missing from cart
      productRemoved = lastCartState.requiredVariants.some((variantId) => {
        const isMissing = !currentManagedVariantIds.has(variantId);
        if (isMissing && !currentlyAddingVariants.has(variantId)) {
          log(`Required variant ${variantId} was removed from cart, will re-add`);
          return true;
        }
        return false;
      });
    }

    if (discountsChanged || !lastCartState || productRemoved) {
      if (productRemoved) {
        log('Required product was removed, re-processing cart...');
      } else {
        log('Cart state changed, debouncing processing...');
      }

      // Clear any pending timeout
      if (processCartTimeout) {
        clearTimeout(processCartTimeout);
        log('Cleared previous processing timeout');
      }

      // Debounce: wait for cart requests to settle before processing
      processCartTimeout = setTimeout(() => {
        processCartTimeout = null;

        if (isProcessing) {
          // Add to queue instead of processing immediately
          processingQueue.push(cartData);
          log('Added to processing queue');
        } else {
          log('Processing cart after debounce...');
          processCart(cartData);
        }
      }, 300); // Increased debounce delay to 300ms
    } else {
      log('Cart state unchanged, skipping processing');
    }
  }

  // Intercept fetch requests
  const originalFetch = window.fetch;
  window.fetch = function (...args) {
    const url = args[0];
    const isCartRequest =
      typeof url === 'string' &&
      (url.includes('/cart/add.js') ||
        url.includes('/cart/update.js') ||
        url.includes('/cart/change.js') ||
        url.includes('/cart.js'));

    if (isCartRequest) {
      pendingRequests++;
      log(`Fetch request to ${url}, pending: ${pendingRequests}`);
    }

    return originalFetch
      .apply(this, args)
      .then((response) => {
        if (isCartRequest) {
          // Clone response to read it
          const clonedResponse = response.clone();

          clonedResponse
            .json()
            .then((data) => {
              pendingRequests--;
              log(`Fetch response from ${url}, pending: ${pendingRequests}`);

              // Only process if no more pending requests
              if (pendingRequests <= 0) {
                pendingRequests = 0;
                handleCartResponse(data);
              }
            })
            .catch((err) => {
              pendingRequests--;
              log('Error parsing fetch response:', err);
            });
        }

        return response;
      })
      .catch((error) => {
        if (isCartRequest) {
          pendingRequests--;
        }
        throw error;
      });
  };

  // Intercept XMLHttpRequest
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._url = url;
    this._method = method;
    return originalOpen.apply(this, [method, url, ...rest]);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    const isCartRequest =
      this._url &&
      (this._url.includes('/cart/add.js') ||
        this._url.includes('/cart/update.js') ||
        this._url.includes('/cart/change.js') ||
        this._url.includes('/cart.js'));

    if (isCartRequest) {
      pendingRequests++;
      log(`XHR request to ${this._url}, pending: ${pendingRequests}`);

      const originalOnReadyStateChange = this.onreadystatechange;
      const originalOnLoad = this.onload;

      this.onreadystatechange = function () {
        if (this.readyState === 4 && isCartRequest) {
          try {
            const data = JSON.parse(this.responseText);
            pendingRequests--;
            log(`XHR response from ${this._url}, pending: ${pendingRequests}`);

            // Only process if no more pending requests
            if (pendingRequests <= 0) {
              pendingRequests = 0;
              handleCartResponse(data);
            }
          } catch (err) {
            pendingRequests--;
            log('Error parsing XHR response:', err);
          }
        }

        if (originalOnReadyStateChange) {
          return originalOnReadyStateChange.apply(this, arguments);
        }
      };

      this.onload = function () {
        if (isCartRequest && !this.onreadystatechange) {
          try {
            const data = JSON.parse(this.responseText);
            pendingRequests--;
            log(`XHR load from ${this._url}, pending: ${pendingRequests}`);

            if (pendingRequests <= 0) {
              pendingRequests = 0;
              handleCartResponse(data);
            }
          } catch (err) {
            pendingRequests--;
            log('Error parsing XHR load response:', err);
          }
        }

        if (originalOnLoad) {
          return originalOnLoad.apply(this, arguments);
        }
      };
    }

    return originalSend.apply(this, args);
  };

  // Initial cart check on page load
  log('Initializing discount auto-add script');
  log('Configuration:', config);

  // Wait for page to be ready and perform initial check
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialCartCheck);
  } else {
    setTimeout(initialCartCheck, 500);
  }

  function initialCartCheck() {
    log('Performing initial cart check...');
    fetch('/cart.js')
      .then((response) => response.json())
      .then((data) => {
        log('Initial cart data:', data);
        handleCartResponse(data);
      })
      .catch((error) => {
        console.error('[Cart-Extra] Failed to fetch initial cart:', error);
      });
  }
})();
